import * as angular from "angular";
import {SiteProviderModel} from "../angular/models/site-provider.model";
import {SiteProviderRequestModel} from "../angular/models/site-provider-request.model";
import {SiteProviderKind} from "../angular/types/site-provider-kind.type";
import {SiteProviderType} from "../angular/types/site-provider.type";
import {
  isPredictionProviderType,
  SITE_PROVIDER_TYPE_LABEL_PREFIX,
  SITE_PROVIDER_TYPE_OPTIONS
} from "../angular/constants/site-provider-types.constant";
import { SplitService } from "../../commons/services/split-service.service";

/**
 * The Providers screen's create / edit dialogs.
 */
interface SiteProviderDialogForm {
  name: string;
  description: string;
  providerType: SiteProviderType;
  dataset: string;
  table: string;
  endpointId: string;
  /** Not editable in the dialog - carried through so saving an edit does not deactivate the provider. */
  active: boolean;
}

/**
 * Maps a providerKind to the matching field on siteInfo.visitPlanProviders (SplitService.getVisitPlanProviders()) -
 * the site's enabled provider types per kind, set via the "Scheduling Project Providers Selections" site preference.
 */
const VISIT_PLAN_PROVIDERS_FIELD: { [key in SiteProviderKind]: string } = {
  [SiteProviderKind.ServiceTime]: 'visitSlotServiceTimeProviders',
  [SiteProviderKind.Units]: 'visitSlotUnitsProviders',
  [SiteProviderKind.DeliveryTime]: 'visitSlotDeliveryTimeProviders'
};

export class SiteProvidersDialogsService {

  /* @ngInject */
  constructor(private $mdDialog, private splitService: SplitService) {
  }

  /**
   * The providerType values enabled for the site for this providerKind - same source the Scheduling
   * Project template dialog reads (getVisitPlanProviders() return type is stale, hence the cast).
   * Falls back to the hardcoded defaults if the site config is missing or empty for this kind.
   */
  private enabledTypesFor(providerKind: SiteProviderKind): SiteProviderType[] {
    const visitPlanProviders = this.splitService.getVisitPlanProviders() as unknown as { [field: string]: SiteProviderType[] };
    const enabledTypes = visitPlanProviders?.[VISIT_PLAN_PROVIDERS_FIELD[providerKind]];
    return enabledTypes && enabledTypes.length > 0 ? enabledTypes : SITE_PROVIDER_TYPE_OPTIONS[providerKind];
  }

  /**
   * @param providerKind the tab the provider belongs to - sent as-is, which is why the form has
   *                     no kind picker.
   * @returns a promise resolving with the request payload, rejected when the dialog is dismissed.
   */
  create(event, providerKind: SiteProviderKind): Promise<SiteProviderRequestModel> {
    return this.show(event, 'Add Provider', 'Submit', providerKind, {
      name: undefined,
      description: undefined,
      providerType: this.enabledTypesFor(providerKind)[0],
      dataset: undefined,
      table: undefined,
      endpointId: undefined,
      active: true
    });
  }

  edit(event, provider: SiteProviderModel): Promise<SiteProviderRequestModel> {
    return this.show(event, 'Edit Provider', 'Save', provider.providerKind, {
      name: provider.name,
      description: provider.description,
      providerType: provider.providerType,
      dataset: provider.dataset,
      table: provider.table,
      endpointId: provider.endpointId,
      active: provider.active
    });
  }

  private show(event,
               header: string,
               action: string,
               providerKind: SiteProviderKind,
               form: Partial<SiteProviderDialogForm>): Promise<SiteProviderRequestModel> {
    const self = this;
    return this.$mdDialog.show({
      parent: angular.element(document.body),
      template: require('./../views/dialogs/site-provider-dialog.html').default,
      controller: function ($scope) {
        $scope.header = header;
        $scope.action = action;
        // bringoz-select translates each option as `<options-prefix>.<value>`, so it takes the
        // raw provider type strings - same wiring as the Scheduling Project template dialog.
        $scope.typeOptions = self.enabledTypesFor(providerKind);
        $scope.typeOptionsPrefix = SITE_PROVIDER_TYPE_LABEL_PREFIX[providerKind];
        $scope.provider = form;
        $scope.cancel = self.$mdDialog.cancel;

        $scope.onSelectType = (providerType: SiteProviderType) => {
          $scope.provider.providerType = providerType;
        };

        // Drives the endpoint field, which only a prediction provider has.
        $scope.isPrediction = () => isPredictionProviderType($scope.provider.providerType);

        $scope.submit = (dialogForm) => {
          if (!dialogForm) {
            return;
          }

          dialogForm.$setSubmitted(true);

          if (dialogForm.$valid) {
            self.$mdDialog.hide(self.buildPayload($scope.provider, providerKind));
          }
        };
      },
      targetEvent: event,
      clickOutsideToClose: true
    });
  }

  private buildPayload(form: SiteProviderDialogForm, providerKind: SiteProviderKind): SiteProviderRequestModel {
    return {
      name: form.name.trim(),
      description: form.description?.trim() || undefined,
      providerKind: providerKind,
      providerType: form.providerType,
      dataset: form.dataset.trim(),
      table: form.table.trim(),
      endpointId: isPredictionProviderType(form.providerType) ? form.endpointId.trim() : undefined,
      active: form.active
    };
  }

}

angular
  .module('bringoz.admin.site-preferences')
  .service('siteProvidersDialogsService', SiteProvidersDialogsService);
